/**
 * Articles / knowledge-base content — original editorial written for True Forge.
 * Listing at /articles, detail at /articles/:slug (prerendered via getStaticPaths).
 * `image` is resolved against the product imageMap in the rendering component.
 */
export interface ArticleSection {
  heading?: string
  body: string[]
}

export interface Article {
  slug: string
  title: string
  excerpt: string
  category: string
  date: string
  readMins: number
  image: { sku: string; index: number }
  sections: ArticleSection[]
}

export const articlesIntro =
  'Practical guidance on welding protection, glove selection, materials and care — written for the professionals and buyers who rely on them.'

export const articles: Article[] = [
  {
    slug: 'understanding-en388-en407-glove-ratings',
    title: 'Understanding EN 388 and EN 407: What Glove Ratings Actually Mean',
    excerpt:
      'The strings of numbers on a safety glove aren’t marketing — they’re a shorthand for measured performance. Here’s how to read them.',
    category: 'Standards',
    date: 'July 2026',
    readMins: 5,
    image: { sku: 'tf120', index: 5 },
    sections: [
      {
        body: [
          'Every certified safety glove carries a set of pictograms and numbers. To a buyer they can look cryptic, but each digit is a tested, comparable performance level. Reading them correctly is the difference between specifying protection that fits the hazard and guessing.',
        ],
      },
      {
        heading: 'EN 388 — mechanical risks',
        body: [
          'EN 388 covers abrasion, cut, tear and puncture. Each is scored on a scale, with higher numbers meaning greater resistance. For handling rough steel, rebar or sharp-edged stock, the cut and abrasion scores matter most; for assembly work, dexterity may take priority over the highest rating.',
          'The point isn’t always to chase the top score — it’s to match the score to the real task, so protection is actually worn and not swapped out for comfort.',
        ],
      },
      {
        heading: 'EN 407 — heat and flame',
        body: [
          'EN 407 rates thermal performance: burning behaviour, contact heat, convective heat, radiant heat and resistance to small and large splashes of molten metal. Welding and foundry work lean heavily on these ratings, particularly contact and radiant heat and molten-metal splash.',
        ],
      },
      {
        heading: 'EN 12477 and EN ISO 11611 — the welding picture',
        body: [
          'EN 12477 is purpose-built for welders’ gloves and typically references both EN 388 and EN 407. EN ISO 11611 covers protective clothing for welding and allied processes. Together they describe a system of protection — glove, sleeve and garment — designed to work as one.',
        ],
      },
      {
        body: [
          'When you specify True Forge welding products, these standards are the baseline the range is engineered to meet — verifiable performance, not a claim on a label.',
        ],
      },
    ],
  },
  {
    slug: 'choosing-the-right-welding-glove',
    title: 'TIG, MIG or Stick: Choosing the Right Welding Glove for the Job',
    excerpt:
      'The best welding glove is the one matched to your process. A quick guide to picking by heat, dexterity and duty.',
    category: 'Selection',
    date: 'July 2026',
    readMins: 4,
    image: { sku: 'tf111', index: 4 },
    sections: [
      {
        body: [
          'No single glove is right for every weld. The three main arc processes place very different demands on the hands, and choosing well comes down to balancing heat protection against the dexterity the work needs.',
        ],
      },
      {
        heading: 'TIG — dexterity first',
        body: [
          'TIG welding is precise, lower-heat work where feel matters. A thinner, softer glove — often goatskin — lets the welder manipulate the filler rod and torch with control. Protection is still essential, but not at the cost of the fine motor skill TIG demands.',
        ],
      },
      {
        heading: 'MIG — the balanced middle',
        body: [
          'MIG produces more heat and spatter than TIG, so the glove needs more insulation and a tougher shell while keeping enough flexibility to feed and position work. This is the everyday fabrication glove for many shops.',
        ],
      },
      {
        heading: 'Stick and heavy fabrication — protection first',
        body: [
          'Stick (SMAW) and heavy structural work generate sustained heat and aggressive spatter. Here the priority flips to maximum insulation and durability — thicker leathers, reinforced palms and cuffs, and heat-resistant linings that hold up shift after shift.',
        ],
      },
      {
        body: [
          'True Forge organises its welding range around exactly these tasks, so matching a glove to your process is straightforward — and if you need something tuned to your specification, we can build it.',
        ],
      },
    ],
  },
  {
    slug: 'leather-types-in-safety-gloves',
    title: 'Leather in Safety Gloves: Cowhide, Goatskin and Split Explained',
    excerpt:
      'Grain, split, cowhide, goatskin — the leather makes the glove. What each brings, and where it belongs.',
    category: 'Materials',
    date: 'July 2026',
    readMins: 4,
    image: { sku: 'tf224', index: 3 },
    sections: [
      {
        body: [
          'Leather is the heart of a protective glove, and not all leather is equal. The type and cut determine how a glove balances heat resistance, durability, grip and dexterity — which is why True Forge selects material by task rather than by cost.',
        ],
      },
      {
        heading: 'Full-grain vs split',
        body: [
          'Full-grain leather is the outer layer of the hide — strong, abrasion-resistant and durable, ideal where wear and heat are highest. Split leather comes from the lower layers; it’s more economical and breathable, and works well for cuffs, backs and lighter-duty areas. Many gloves combine both, placing full-grain where protection matters most.',
        ],
      },
      {
        heading: 'Cowhide — the workhorse',
        body: [
          'Cowhide is durable, heat-resistant and widely used across welding and heavy handling. It stands up to sustained abrasion and delivers dependable all-round protection, making it the default for demanding environments.',
        ],
      },
      {
        heading: 'Goatskin — dexterity and feel',
        body: [
          'Goatskin is naturally strong yet supple, offering excellent tactile feedback. Where a task needs fine control — TIG welding, detailed assembly — goatskin gives the dexterity heavier leathers can’t.',
        ],
      },
      {
        body: [
          'Choosing the right leather isn’t about the most expensive option — it’s about the right material in the right place. That deliberate selection is what keeps True Forge gloves protective and comfortable enough to wear all shift.',
        ],
      },
    ],
  },
  {
    slug: 'caring-for-welding-gloves',
    title: 'Making Gloves Last: Care and Maintenance for Welding PPE',
    excerpt:
      'Good welding gloves are an investment. A few simple habits keep them protective for longer.',
    category: 'Care',
    date: 'July 2026',
    readMins: 3,
    image: { sku: 'tf129', index: 4 },
    sections: [
      {
        body: [
          'A quality welding glove protects best when it’s looked after. Leather is a natural material — how it’s dried, stored and inspected has a direct effect on how long it keeps its protective qualities.',
        ],
      },
      {
        heading: 'Dry gently, never force heat',
        body: [
          'After use, let gloves air-dry naturally at room temperature. Drying leather too fast — on a radiator or in direct heat — stiffens and cracks it, weakening exactly the protection you rely on.',
        ],
      },
      {
        heading: 'Keep them clean and dry in storage',
        body: [
          'Store gloves in a cool, dry place away from damp and direct sunlight. Keep them free of oil, grease and grinding dust, which degrade leather and reduce grip over time.',
        ],
      },
      {
        heading: 'Inspect and retire on schedule',
        body: [
          'Check seams, palms, thumbs and cuffs regularly for wear, thinning or heat damage. A glove that has lost integrity is no longer protective — retire it promptly rather than pushing it one more shift.',
        ],
      },
      {
        body: [
          'Simple care extends the life of good PPE and keeps your team properly protected. When it’s time to replace, True Forge supplies consistent, dependable stock built for the work.',
        ],
      },
    ],
  },
]

export const getArticle = (slug?: string): Article | undefined => articles.find((a) => a.slug === slug)
